import { usePlayerEngine } from "../../../runtime/provider";
import { usePlayerStoreApi } from "@utils/player/core/usePlayer";
import { useEffect } from "react";

import { selectCurrentItem } from "../core/selectors";
import type { PlaybackStatus } from "../core/types";

const RESUME_CHECKPOINT_INTERVAL_MS = 5_000;
const RESUME_CHECKPOINT_SEEK_JUMP_MS = 10_000;

/**
 * Hook that observes AudioPlayer state changes and syncs them to the store.
 * This is a pure observer - it never calls playback commands.
 */
export function useAudioPlayerState(): void {
  const player = usePlayerEngine();
  const audioPlayer = player.audio;
  const { ensureProgressForPlaybackTarget, updateNativeProgress } =
    player.progress;
  const { writePlaybackResumeCheckpoint } = player.resume;

  const playerStore = usePlayerStoreApi();

  useEffect(() => {
    let lastStatus: PlaybackStatus | null = null;
    let lastPlaybackState: string | null = null;
    let lastErrCode: number | undefined;
    let lastErrMsg: string | undefined;
    let lastErrSev: string | undefined;
    let lastSyncedTrackId: string | null = null;
    let lastSyncedQueueItemId: string | null = null;
    let lastCheckpointAtMs = 0;
    let lastCheckpointPositionMs = 0;
    let lastCheckpointQueueItemId: string | null = null;
    let lastCheckpointPlaybackState: string | null = null;

    const unsubscribe = audioPlayer.on((state) => {
      const playerState = playerStore.getState();
      const currentItem = selectCurrentItem(playerState);
      const activeTrackId = audioPlayer.activeTrackId;
      const activeQueueItemId = audioPlayer.activeQueueItemId;

      // Queue selection changes before async preparation completes. Native
      // events can still arrive for the previous song during that window;
      // never let those events drive the displayed song's status or progress.
      if (
        currentItem &&
        (activeTrackId !== currentItem.metadata.id ||
          (activeQueueItemId != null && activeQueueItemId !== currentItem.qid))
      ) {
        return;
      }

      const syncedQueueItemId = activeQueueItemId ?? currentItem?.qid ?? null;
      if (
        lastSyncedTrackId !== activeTrackId ||
        lastSyncedQueueItemId !== syncedQueueItemId
      ) {
        lastSyncedTrackId = activeTrackId;
        lastSyncedQueueItemId = syncedQueueItemId;
        lastStatus = null;
        lastPlaybackState = null;
        lastErrCode = undefined;
        lastErrMsg = undefined;
        lastErrSev = undefined;
      }

      // Sync playback status to store
      let storeStatus: PlaybackStatus = "IDLE";
      switch (state.playbackState) {
        case "playing":
          storeStatus = "PLAYING";
          break;
        case "paused":
          storeStatus = "PAUSED";
          break;
        case "loading":
          storeStatus = "LOADING";
          break;
        case "error":
          storeStatus = "ERROR";
          break;
        case "idle":
          storeStatus = "IDLE";
          break;
      }

      // 💡 Smoothing logic using userIntent (single source of truth):
      // When switching songs, the engine briefly hits 'idle'.
      // To prevent the Play/Pause button from flickering, we show 'LOADING'
      // during these transient states if the user's intent is to keep playing.
      const userIntent = playerState.userIntent;
      if (state.playbackState === "idle" && userIntent === "playing") {
        storeStatus = "LOADING";
      }

      // PERF: status should only update on actual state changes.
      // AudioPro emits frequent PROGRESS events; avoid writing to the store each time.
      const playbackStateStr = state.playbackState;
      if (
        storeStatus !== lastStatus ||
        playbackStateStr !== lastPlaybackState
      ) {
        lastStatus = storeStatus;
        lastPlaybackState = playbackStateStr;
        playerStore.setState(
          (s) => {
            s.status = storeStatus;
          },
          false,
          "audioPlayer/syncStatus",
        );
      }

      // Sync progress to shared values (Reanimated)
      if (currentItem) {
        ensureProgressForPlaybackTarget(currentItem);
      }
      updateNativeProgress(
        state.positionMs,
        state.durationMs,
        state.bufferedPositionMs,
        state.isPositionConfirmed !== false,
      );

      // Keep the high-frequency UI position in Reanimated, but persist one
      // small, identity-bound checkpoint at a coarse cadence. Large seeks and
      // pauses flush immediately so a process exit cannot restore an older
      // point from the same queue occurrence.
      if (
        currentItem &&
        activeTrackId === currentItem.metadata.id &&
        (activeQueueItemId == null || activeQueueItemId === currentItem.qid) &&
        lastSyncedQueueItemId === currentItem.qid &&
        (state.playbackState === "playing" ||
          state.playbackState === "paused") &&
        state.isPositionConfirmed !== false &&
        Number.isFinite(state.positionMs) &&
        state.positionMs > 0
      ) {
        const nowMs = Date.now();
        const queueItemChanged = lastCheckpointQueueItemId !== currentItem.qid;
        const positionJumped =
          Math.abs(state.positionMs - lastCheckpointPositionMs) >=
          RESUME_CHECKPOINT_SEEK_JUMP_MS;
        const intervalElapsed =
          nowMs - lastCheckpointAtMs >= RESUME_CHECKPOINT_INTERVAL_MS;
        const enteredPaused =
          state.playbackState === "paused" &&
          lastCheckpointPlaybackState !== "paused";

        if (
          queueItemChanged ||
          positionJumped ||
          intervalElapsed ||
          enteredPaused
        ) {
          writePlaybackResumeCheckpoint({
            queueType: playerState.activeQueueType,
            songId: currentItem.metadata.id,
            queueItemId: currentItem.qid,
            positionMs: state.positionMs,
            durationMs:
              state.durationMs > 0
                ? state.durationMs
                : (currentItem.metadata.duration ?? 0) * 1000,
            updatedAtMs: nowMs,
          });
          lastCheckpointAtMs = nowMs;
          lastCheckpointPositionMs = state.positionMs;
          lastCheckpointQueueItemId = currentItem.qid;
        }
        lastCheckpointPlaybackState = state.playbackState;
      }

      // Sync lastError to store (explicit field comparison, no JSON.stringify)
      const err = state.lastError;
      if (
        err?.code !== lastErrCode ||
        err?.message !== lastErrMsg ||
        err?.severity !== lastErrSev
      ) {
        lastErrCode = err?.code;
        lastErrMsg = err?.message;
        lastErrSev = err?.severity;
        playerStore.getState().actions.setPlaybackLastError(err ?? null);
      }
    });

    return () => {
      unsubscribe();
    };
  }, [
    player,
    playerStore,
    audioPlayer,
    ensureProgressForPlaybackTarget,
    updateNativeProgress,
    writePlaybackResumeCheckpoint,
  ]);
}
